/**
 * LoopNest AI Generation Guard System
 * 
 * Prevents uncontrolled API usage and protects against extremely high AI billing.
 * All generation jobs MUST pass through this guard before any GPU work begins.
 *
 * Guards:
 * 1. Authentication check
 * 2. Subscription plan validation
 * 3. Video seconds quota check
 * 4. Credit (coin) balance check
 * 5. Rate limit enforcement (3/min, 10/10min, 30/hour)
 * 6. Generation job lock (1 active job per user)
 * 7. Abuse detection (20+ requests in 5min, repeated failures, bot patterns)
 * 8. GPU cost estimation vs quota
 * 9. Queue enforcement (all jobs must go through queue)
 *
 * Platform Owner Exception:
 * 365ddevotional@gmail.com bypasses rate limits but still passes authentication.
 */

import { supabase } from '../lib/supabase';
import { isPlatformOwnerEmail } from '../utils/adminOverride';
import { VIDEO_SECONDS_PLANS, COIN_COSTS } from './generationPipeline';
import { checkMusicQuota, checkGameQuota, getComputeWalletStatus } from './computeWallet';

// ─── RATE LIMIT CONFIGURATION ────────────────────────────────────────────────

export const RATE_LIMITS = {
  per_minute: 3,        // max 3 requests per minute
  per_10_minutes: 10,   // max 10 requests per 10 minutes
  per_hour: 30,         // max 30 requests per hour
  per_day: 100,         // max 100 requests per day
};

// ─── ABUSE DETECTION THRESHOLDS ──────────────────────────────────────────────

export const ABUSE_THRESHOLDS = {
  requests_per_5_min: 20,   // flag if > 20 requests in 5 minutes
  consecutive_failures: 5,  // flag after 5 consecutive failures
  bot_pattern_interval_ms: 800, // requests faster than 800ms apart = bot pattern
};

// ─── GPU COST ESTIMATION ─────────────────────────────────────────────────────
// Keyframe-based cost model:
//   Cost = keyframes_generated × cost_per_keyframe_image
//   Max keyframes per video = 8 (KEYFRAME_CONFIG.max_keyframes_per_video)
//   Max cost = 8 × $0.002 = $0.016 per video (extremely low)
//   Frame interpolation adds ~$0.0005 per scene (no new AI frames)

export const GPU_COST_PER_KEYFRAME_USD = 0.002;      // ~$0.002 per Stable Diffusion keyframe image
export const GPU_COST_INTERPOLATION_USD = 0.0005;    // ~$0.0005 per scene for frame interpolation
export const MAX_KEYFRAMES_PER_VIDEO = 8;            // hard limit from KEYFRAME_CONFIG
export const MAX_COST_PER_JOB_USD = 0.50;           // hard cap: reject if estimated cost > $0.50

// Legacy constant kept for backward compatibility
export const GPU_COST_PER_SECOND_USD = 0.001;

/**
 * Estimate GPU cost for a generation job using the keyframe model.
 * Cost is based on number of keyframes generated, NOT video duration.
 * This ensures costs stay extremely low regardless of video length.
 *
 * Formula:
 *   keyframes = min(8, ceil(durationSeconds / 7.5))
 *   cost = keyframes × $0.002 + keyframes × $0.0005 (interpolation)
 *
 * @param {number} durationSeconds - requested video duration
 * @param {string} styleId - visual style (affects complexity multiplier)
 * @returns {{ estimatedCostUsd: number, withinBudget: boolean, maxCostUsd: number, keyframeCount: number, breakdown: object }}
 */
export function estimateGpuCost(durationSeconds, styleId = 'cinematic-stock') {
  // Calculate keyframe count from duration (capped at max)
  const secondsPerKeyframe = 7.5;
  const naturalKeyframes = Math.ceil(durationSeconds / secondsPerKeyframe);
  const keyframeCount = Math.min(MAX_KEYFRAMES_PER_VIDEO, Math.max(5, naturalKeyframes));

  // Realistic styles cost slightly more (higher quality SD settings)
  const realisticStyles = new Set([
    'realistic-photos', 'cinematic-stock', 'nature', 'urban', 'documentary',
    'worship', 'realistic-captions', 'realistic-beats', 'visualizer-cinematic',
  ]);
  const costMultiplier = realisticStyles?.has(styleId) ? 1.2 : 1.0;

  // Keyframe image generation cost
  const imageCost = keyframeCount * GPU_COST_PER_KEYFRAME_USD * costMultiplier;
  // Frame interpolation cost (no new AI frames — just motion transforms)
  const interpolationCost = keyframeCount * GPU_COST_INTERPOLATION_USD;
  // Assembly cost (fixed)
  const assemblyCost = 0.002;

  const estimatedCostUsd = imageCost + interpolationCost + assemblyCost;

  return {
    estimatedCostUsd: Math.round(estimatedCostUsd * 10000) / 10000,
    withinBudget: estimatedCostUsd <= MAX_COST_PER_JOB_USD,
    maxCostUsd: MAX_COST_PER_JOB_USD,
    keyframeCount,
    breakdown: {
      keyframeImages: `${keyframeCount} × $${GPU_COST_PER_KEYFRAME_USD} = $${imageCost?.toFixed(4)}`,
      frameInterpolation: `${keyframeCount} scenes × $${GPU_COST_INTERPOLATION_USD} = $${interpolationCost?.toFixed(4)}`,
      assembly: `$${assemblyCost}`,
      total: `$${estimatedCostUsd?.toFixed(4)}`,
      renderingMode: 'keyframe_interpolation',
    },
  };
}

// ─── IN-MEMORY RATE LIMIT STORE ──────────────────────────────────────────────
// Client-side rate limit tracking (backed by DB for server-side enforcement)

const _rateLimitStore = new Map(); // userId → { timestamps: number[] }

function _getRateLimitEntry(userId) {
  if (!_rateLimitStore?.has(userId)) {
    _rateLimitStore?.set(userId, { timestamps: [], lastRequestMs: 0 });
  }
  return _rateLimitStore?.get(userId);
}

/**
 * Client-side rate limit check.
 * Returns { allowed, reason, retryAfterSeconds }
 */
export function checkClientRateLimit(userId, userEmail = '') {
  // Platform owner bypasses rate limits
  if (isPlatformOwnerEmail(userEmail)) {
    return { allowed: true, reason: 'platform_owner_bypass' };
  }

  const now = Date.now();
  const entry = _getRateLimitEntry(userId);

  // Prune timestamps older than 1 hour
  entry.timestamps = entry?.timestamps?.filter((t) => now - t < 3600_000);

  // Check bot pattern: requests faster than threshold
  if (entry?.lastRequestMs > 0 && now - entry?.lastRequestMs < ABUSE_THRESHOLDS?.bot_pattern_interval_ms) {
    return {
      allowed: false,
      reason: 'bot_pattern_detected',
      message: 'Requests are being sent too quickly. Please wait before trying again.',
      retryAfterSeconds: 5,
    };
  }

  const oneMinuteAgo = now - 60_000;
  const tenMinutesAgo = now - 600_000;
  const oneHourAgo = now - 3600_000;
  const oneDayAgo = now - 86400_000;

  const countLastMinute = entry?.timestamps?.filter((t) => t > oneMinuteAgo)?.length;
  const countLast10Min = entry?.timestamps?.filter((t) => t > tenMinutesAgo)?.length;
  const countLastHour = entry?.timestamps?.filter((t) => t > oneHourAgo)?.length;
  const countLastDay = entry?.timestamps?.filter((t) => t > oneDayAgo)?.length;

  if (countLastMinute >= RATE_LIMITS?.per_minute) {
    const oldestInWindow = entry?.timestamps?.filter((t) => t > oneMinuteAgo)?.[0];
    const retryAfterSeconds = Math.ceil((oldestInWindow + 60_000 - now) / 1000);
    return {
      allowed: false,
      reason: 'rate_limit_per_minute',
      message: 'Generation limit reached. Please wait before generating again.',
      retryAfterSeconds,
    };
  }

  if (countLast10Min >= RATE_LIMITS?.per_10_minutes) {
    const oldestInWindow = entry?.timestamps?.filter((t) => t > tenMinutesAgo)?.[0];
    const retryAfterSeconds = Math.ceil((oldestInWindow + 600_000 - now) / 1000);
    return {
      allowed: false,
      reason: 'rate_limit_per_10_minutes',
      message: 'Generation limit reached. Please wait before generating again.',
      retryAfterSeconds,
    };
  }

  if (countLastHour >= RATE_LIMITS?.per_hour) {
    const oldestInWindow = entry?.timestamps?.filter((t) => t > oneHourAgo)?.[0];
    const retryAfterSeconds = Math.ceil((oldestInWindow + 3600_000 - now) / 1000);
    return {
      allowed: false,
      reason: 'rate_limit_per_hour',
      message: 'Generation limit reached. Please wait before generating again.',
      retryAfterSeconds,
    };
  }

  if (countLastDay >= RATE_LIMITS?.per_day) {
    return {
      allowed: false,
      reason: 'rate_limit_per_day',
      message: 'Generation limit reached. Please wait before generating again.',
      retryAfterSeconds: 3600,
    };
  }

  return { allowed: true, reason: 'ok' };
}

/**
 * Record a generation attempt in the client-side rate limit store.
 */
export function recordGenerationAttempt(userId) {
  const now = Date.now();
  const entry = _getRateLimitEntry(userId);
  entry?.timestamps?.push(now);
  entry.lastRequestMs = now;
  // Keep only last hour
  entry.timestamps = entry?.timestamps?.filter((t) => now - t < 3600_000);
}

// ─── DB-BACKED RATE LIMIT CHECK ───────────────────────────────────────────────

/**
 * Check rate limits against the database (server-side enforcement).
 * Falls back gracefully if DB is unavailable.
 */
async function checkDbRateLimit(userId, userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) {
    return { allowed: true, reason: 'platform_owner_bypass' };
  }

  try {
    const now = new Date();
    const oneMinuteAgo = new Date(now - 60_000)?.toISOString();
    const tenMinutesAgo = new Date(now - 600_000)?.toISOString();
    const oneHourAgo = new Date(now - 3600_000)?.toISOString();

    const { data, error } = await supabase?.from('generation_rate_log')?.select('created_at')?.eq('user_id', userId)?.gte('created_at', oneHourAgo)?.order('created_at', { ascending: false });

    if (error) throw error;

    const timestamps = (data || [])?.map((r) => new Date(r.created_at)?.getTime());
    const nowMs = now?.getTime();

    const countLastMinute = timestamps?.filter((t) => t > nowMs - 60_000)?.length;
    const countLast10Min = timestamps?.filter((t) => t > nowMs - 600_000)?.length;
    const countLastHour = timestamps?.length;

    if (countLastMinute >= RATE_LIMITS?.per_minute) {
      return {
        allowed: false,
        reason: 'rate_limit_per_minute',
        message: `Rate limit exceeded: max ${RATE_LIMITS?.per_minute} generations per minute.`,
      };
    }
    if (countLast10Min >= RATE_LIMITS?.per_10_minutes) {
      return {
        allowed: false,
        reason: 'rate_limit_per_10_minutes',
        message: `Rate limit exceeded: max ${RATE_LIMITS?.per_10_minutes} generations per 10 minutes.`,
      };
    }
    if (countLastHour >= RATE_LIMITS?.per_hour) {
      return {
        allowed: false,
        reason: 'rate_limit_per_hour',
        message: `Rate limit exceeded: max ${RATE_LIMITS?.per_hour} generations per hour.`,
      };
    }

    return { allowed: true, reason: 'ok', counts: { minute: countLastMinute, tenMin: countLast10Min, hour: countLastHour } };
  } catch (err) {
    console.warn('[GenerationGuard] DB rate limit check failed, using client-side only:', err?.message);
    return { allowed: true, reason: 'db_unavailable_fail_open' };
  }
}

/**
 * Record a rate limit entry in the database.
 */
async function recordDbRateLimit(userId, jobId = null) {
  try {
    await supabase?.from('generation_rate_log')?.insert({
      user_id: userId,
      job_id: jobId,
      created_at: new Date()?.toISOString(),
    });
  } catch (err) {
    console.warn('[GenerationGuard] Failed to record rate limit entry:', err?.message);
  }
}

// ─── GENERATION JOB LOCK ─────────────────────────────────────────────────────

/**
 * Acquire a generation lock for a user.
 * Only ONE active generation job is allowed per user at a time.
 * Returns { acquired: boolean, lockId: string|null, existingJobId: string|null }
 */
export async function acquireGenerationLock(userId, userEmail = '') {
  // Platform owner can bypass lock (for testing)
  if (isPlatformOwnerEmail(userEmail)) {
    return { acquired: true, lockId: `owner-lock-${Date.now()}`, existingJobId: null };
  }

  try {
    // Check for existing active lock
    const { data: existingLock, error: checkError } = await supabase?.from('generation_locks')?.select('id, job_id, created_at')?.eq('user_id', userId)?.eq('is_active', true)?.single();

    if (checkError && checkError?.code !== 'PGRST116') {
      // PGRST116 = no rows found, which is fine
      throw checkError;
    }

    if (existingLock) {
      // Check if lock is stale (older than 10 minutes — safety valve)
      const lockAge = Date.now() - new Date(existingLock.created_at)?.getTime();
      if (lockAge > 600_000) {
        // Stale lock — release it automatically
        await releaseGenerationLock(userId, existingLock?.id);
      } else {
        return {
          acquired: false,
          lockId: null,
          existingJobId: existingLock?.job_id,
          message: 'You already have an active generation job in progress. Please wait for it to complete.',
        };
      }
    }

    // Acquire new lock
    const { data: newLock, error: insertError } = await supabase?.from('generation_locks')?.insert({
        user_id: userId,
        is_active: true,
        created_at: new Date()?.toISOString(),
      })?.select()?.single();

    if (insertError) throw insertError;

    return { acquired: true, lockId: newLock?.id, existingJobId: null };
  } catch (err) {
    console.warn('[GenerationGuard] Lock acquisition failed (fail open):', err?.message);
    // Fail open — don't block generation if lock system is unavailable
    return { acquired: true, lockId: null, existingJobId: null };
  }
}

/**
 * Release a generation lock after job completes or fails.
 */
export async function releaseGenerationLock(userId, lockId = null) {
  try {
    let query = supabase?.from('generation_locks')?.update({ is_active: false, released_at: new Date()?.toISOString() })?.eq('user_id', userId)?.eq('is_active', true);

    if (lockId) {
      query = query?.eq('id', lockId);
    }

    await query;
  } catch (err) {
    console.warn('[GenerationGuard] Lock release failed:', err?.message);
  }
}

/**
 * Update a lock with the associated job ID.
 */
export async function updateLockWithJobId(lockId, jobId) {
  if (!lockId || !jobId) return;
  try {
    await supabase?.from('generation_locks')?.update({ job_id: jobId })?.eq('id', lockId);
  } catch (err) {
    console.warn('[GenerationGuard] Failed to update lock with job ID:', err?.message);
  }
}

// ─── ABUSE DETECTION ─────────────────────────────────────────────────────────

/**
 * Check if a user account is flagged for abuse.
 * Returns { flagged: boolean, reason: string }
 */
export async function checkAbuseFlag(userId, userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) {
    return { flagged: false, reason: 'platform_owner' };
  }

  try {
    const { data, error } = await supabase?.from('generation_abuse_flags')?.select('id, reason, flagged_at, is_active')?.eq('user_id', userId)?.eq('is_active', true)?.single();

    if (error && error?.code !== 'PGRST116') throw error;

    if (data) {
      return {
        flagged: true,
        reason: data?.reason || 'Account flagged for suspicious generation activity.',
        flaggedAt: data?.flagged_at,
      };
    }

    return { flagged: false, reason: 'ok' };
  } catch (err) {
    console.warn('[GenerationGuard] Abuse flag check failed:', err?.message);
    return { flagged: false, reason: 'check_failed' };
  }
}

/**
 * Evaluate and potentially flag a user for abuse.
 * Called after each generation attempt.
 */
export async function evaluateAbusePattern(userId, userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) return;

  try {
    const fiveMinutesAgo = new Date(Date.now() - 300_000)?.toISOString();

    // Count requests in last 5 minutes
    const { data: recentRequests } = await supabase?.from('generation_rate_log')?.select('id')?.eq('user_id', userId)?.gte('created_at', fiveMinutesAgo);

    const requestCount = recentRequests?.length || 0;

    if (requestCount > ABUSE_THRESHOLDS?.requests_per_5_min) {
      await flagUserForAbuse(userId, `Exceeded ${ABUSE_THRESHOLDS?.requests_per_5_min} generation requests in 5 minutes (count: ${requestCount})`);
      return;
    }

    // Count consecutive failures
    const { data: recentJobs } = await supabase?.from('generation_jobs')?.select('status')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(10);

    if (recentJobs?.length >= ABUSE_THRESHOLDS?.consecutive_failures) {
      const lastN = recentJobs?.slice(0, ABUSE_THRESHOLDS?.consecutive_failures);
      const allFailed = lastN?.every((j) => j?.status === 'failed');
      if (allFailed) {
        await flagUserForAbuse(userId, `${ABUSE_THRESHOLDS?.consecutive_failures} consecutive generation failures detected.`);
      }
    }
  } catch (err) {
    console.warn('[GenerationGuard] Abuse evaluation failed:', err?.message);
  }
}

/**
 * Flag a user account for abuse — disables generation temporarily.
 */
async function flagUserForAbuse(userId, reason) {
  try {
    // Check if already flagged
    const { data: existing } = await supabase?.from('generation_abuse_flags')?.select('id')?.eq('user_id', userId)?.eq('is_active', true)?.single();

    if (existing) return; // Already flagged

    await supabase?.from('generation_abuse_flags')?.insert({
      user_id: userId,
      reason,
      is_active: true,
      flagged_at: new Date()?.toISOString(),
      // Auto-expire after 1 hour
      expires_at: new Date(Date.now() + 3600_000)?.toISOString(),
    });

    console.warn(`[GenerationGuard] User ${userId} flagged for abuse: ${reason}`);
  } catch (err) {
    console.warn('[GenerationGuard] Failed to flag user for abuse:', err?.message);
  }
}

// ─── CREDIT LOCKING ──────────────────────────────────────────────────────────

/**
 * Reserve (lock) credits for a generation job.
 * Credits are held until the job completes or fails.
 * Returns { locked: boolean, lockId: string|null, amount: number }
 */
export async function lockCreditsForJob(userId, durationSeconds, jobId = null, userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) {
    return { locked: true, lockId: null, amount: 0, bypassed: true };
  }

  const amount = durationSeconds; // 1 credit = 1 second

  try {
    const { data, error } = await supabase?.from('credit_locks')?.insert({
        user_id: userId,
        job_id: jobId,
        amount_locked: amount,
        is_active: true,
        created_at: new Date()?.toISOString(),
      })?.select()?.single();

    if (error) throw error;

    return { locked: true, lockId: data?.id, amount };
  } catch (err) {
    console.warn('[GenerationGuard] Credit lock failed (non-fatal):', err?.message);
    return { locked: true, lockId: null, amount }; // Fail open
  }
}

/**
 * Release a credit lock after job completes.
 */
export async function releaseCreditLock(lockId, userId) {
  if (!lockId) return;
  try {
    await supabase?.from('credit_locks')?.update({ is_active: false, released_at: new Date()?.toISOString() })?.eq('id', lockId)?.eq('user_id', userId);
  } catch (err) {
    console.warn('[GenerationGuard] Credit lock release failed:', err?.message);
  }
}

// ─── MAIN PREFLIGHT GUARD ────────────────────────────────────────────────────

/**
 * Run all preflight safety checks before allowing generation to begin.
 *
 * Checks (in order):
 * 1. Authentication
 * 2. Abuse flag
 * 3. Client-side rate limit
 * 4. DB-backed rate limit
 * 5. Subscription plan validation
 * 6. Duration cap enforcement (video only)
 * 7. Compute quota check (video seconds / music count / game count)
 * 8. Coin balance
 * 9. GPU cost estimation
 * 10. Generation job lock
 *
 * @param {object} params
 * @param {object} params.user - Supabase auth user object
 * @param {string} params.userTier - 'free' | 'creator' | 'studio' | 'pro_creator' | 'enterprise'
 * @param {number} params.requestedDurationSeconds - video duration requested (video only)
 * @param {string} params.styleId - visual style
 * @param {string} params.jobType - 'full_music_video' | 'music_generation' | 'game_generation'
 * @returns {Promise<{ passed: boolean, blockReason: string|null, lockId: string|null, creditLockId: string|null, warnings: string[] }>}
 */
export async function runGenerationPreflight(params) {
  const {
    user,
    userTier = 'free',
    requestedDurationSeconds = 30,
    styleId = 'cinematic-stock',
    jobType = 'full_music_video',
  } = params;

  const warnings = [];
  const userEmail = user?.email || '';
  const userId = user?.id;
  const isPlatformOwner = isPlatformOwnerEmail(userEmail);

  // Determine generation category from jobType
  const isVideoJob = jobType === 'full_music_video' || jobType === 'image' || jobType === 'animation_clip' || jobType === 'scene_batch';
  const isMusicJob = jobType === 'music_generation';
  const isGameJob = jobType === 'game_generation';

  // ── Check 1: Authentication ──
  if (!userId || !user) {
    return {
      passed: false,
      blockReason: 'You must be signed in to generate content. Please log in and try again.',
      lockId: null,
      creditLockId: null,
      warnings,
    };
  }

  // ── Check 2: Abuse flag ──
  const abuseCheck = await checkAbuseFlag(userId, userEmail);
  if (abuseCheck?.flagged) {
    return {
      passed: false,
      blockReason: `Your account has been temporarily suspended from generation: ${abuseCheck?.reason} Please contact support if you believe this is an error.`,
      lockId: null,
      creditLockId: null,
      warnings,
    };
  }

  // ── Check 3: Client-side rate limit ──
  const clientRateCheck = checkClientRateLimit(userId, userEmail);
  if (!clientRateCheck?.allowed) {
    return {
      passed: false,
      blockReason: clientRateCheck?.message,
      lockId: null,
      creditLockId: null,
      warnings,
      retryAfterSeconds: clientRateCheck?.retryAfterSeconds,
    };
  }

  // ── Check 4: DB-backed rate limit (skip for platform owner) ──
  if (!isPlatformOwner) {
    const dbRateCheck = await checkDbRateLimit(userId, userEmail);
    if (!dbRateCheck?.allowed) {
      return {
        passed: false,
        blockReason: dbRateCheck?.message,
        lockId: null,
        creditLockId: null,
        warnings,
      };
    }
  }

  // ── Check 5: Subscription plan validation ──
  const plan = VIDEO_SECONDS_PLANS?.[userTier] || VIDEO_SECONDS_PLANS?.free;
  if (!plan) {
    return {
      passed: false,
      blockReason: 'Invalid subscription plan. Please refresh the page and try again.',
      lockId: null,
      creditLockId: null,
      warnings,
    };
  }

  // ── Check 6: Duration cap enforcement (video jobs only) ──
  if (!isPlatformOwner && isVideoJob && requestedDurationSeconds > plan?.maxVideoSeconds) {
    return {
      passed: false,
      blockReason: `Your ${plan?.label} plan allows a maximum of ${plan?.maxVideoSeconds} seconds per video. You requested ${requestedDurationSeconds}s. Upgrade your plan to generate longer videos.`,
      lockId: null,
      creditLockId: null,
      warnings,
    };
  }

  // ── Check 7: Compute quota check ──
  if (!isPlatformOwner) {
    try {
      const wallet = await getComputeWalletStatus(userId, userTier, userEmail);

      if (isVideoJob) {
        const remaining = wallet?.videoSecondsRemaining ?? plan?.monthlySeconds;
        if (requestedDurationSeconds > remaining) {
          return {
            passed: false,
            blockReason: `Insufficient video seconds quota. You have ${remaining}s remaining but this video requires ${requestedDurationSeconds}s. Upgrade your plan or wait for your quota to reset.`,
            lockId: null,
            creditLockId: null,
            warnings,
          };
        }
        const quota = wallet?.videoSecondsQuota ?? plan?.monthlySeconds;
        if (remaining / quota < 0.2) {
          warnings?.push(`Low video quota: ${remaining}s remaining out of ${quota}s.`);
        }
      } else if (isMusicJob) {
        const musicCheck = await checkMusicQuota(userId, userTier, userEmail);
        if (!musicCheck?.allowed) {
          return {
            passed: false,
            blockReason: musicCheck?.message || 'Music generation quota exceeded. Upgrade your plan for more music generations.',
            lockId: null,
            creditLockId: null,
            warnings,
          };
        }
        if (musicCheck?.remaining <= 2) {
          warnings?.push(`Low music quota: ${musicCheck?.remaining} generations remaining.`);
        }
      } else if (isGameJob) {
        const gameCheck = await checkGameQuota(userId, userTier, userEmail);
        if (!gameCheck?.allowed) {
          return {
            passed: false,
            blockReason: gameCheck?.message || 'Game generation quota exceeded. Upgrade your plan for more game generations.',
            lockId: null,
            creditLockId: null,
            warnings,
          };
        }
        if (gameCheck?.remaining <= 1) {
          warnings?.push(`Low game quota: ${gameCheck?.remaining} generation(s) remaining.`);
        }
      }
    } catch (err) {
      console.warn('[GenerationGuard] Compute quota check failed (fail open):', err?.message);
      warnings?.push('Could not verify compute quota — proceeding with caution.');
    }
  }

  // ── Check 8: Coin balance ──
  if (!isPlatformOwner) {
    try {
      const required = COIN_COSTS?.[jobType] || COIN_COSTS?.full_music_video;
      const { data: coinData } = await supabase?.from('coin_balances')?.select('balance')?.eq('user_id', userId)?.single();

      const balance = coinData?.balance ?? 0;
      if (balance < required) {
        return {
          passed: false,
          blockReason: `Insufficient coins. You need ${required} coins but have ${balance}. Purchase more coins on the Pricing page.`,
          lockId: null,
          creditLockId: null,
          warnings,
        };
      }
    } catch (err) {
      console.warn('[GenerationGuard] Coin balance check failed (fail open):', err?.message);
      warnings?.push('Could not verify coin balance — proceeding with caution.');
    }
  }

  // ── Check 9: GPU cost estimation (video jobs only) ──
  if (isVideoJob) {
    const costEstimate = estimateGpuCost(requestedDurationSeconds, styleId);
    if (!costEstimate?.withinBudget) {
      return {
        passed: false,
        blockReason: `Estimated GPU cost ($${costEstimate?.estimatedCostUsd?.toFixed(4)}) exceeds the per-job limit ($${costEstimate?.maxCostUsd}). Please reduce the video duration and try again.`,
        lockId: null,
        creditLockId: null,
        warnings,
      };
    }
  }

  // ── Check 10: Generation job lock ──
  const lockResult = await acquireGenerationLock(userId, userEmail);
  if (!lockResult?.acquired) {
    return {
      passed: false,
      blockReason: lockResult?.message || 'You already have an active generation job. Please wait for it to complete.',
      lockId: null,
      creditLockId: null,
      warnings,
      existingJobId: lockResult?.existingJobId,
    };
  }

  // ── All checks passed — record attempt ──
  recordGenerationAttempt(userId);
  await recordDbRateLimit(userId);

  // Evaluate abuse patterns asynchronously (non-blocking)
  evaluateAbusePattern(userId, userEmail)?.catch(() => {});

  return {
    passed: true,
    blockReason: null,
    lockId: lockResult?.lockId,
    creditLockId: null,
    warnings,
    costEstimate: isVideoJob ? estimateGpuCost(requestedDurationSeconds, styleId) : null,
    plan,
  };
}

/**
 * Complete the guard after a job is created:
 * - Update lock with job ID
 * - Lock credits
 */
export async function completeGuardSetup(lockId, userId, jobId, durationSeconds, userEmail = '') {
  await updateLockWithJobId(lockId, jobId);
  const creditLock = await lockCreditsForJob(userId, durationSeconds, jobId, userEmail);
  return { creditLockId: creditLock?.lockId };
}

/**
 * Release all guard resources after a job completes or fails.
 * Call this in BOTH success and failure paths.
 */
export async function releaseGuardResources(userId, lockId, creditLockId, userEmail = '') {
  await Promise.allSettled([
    releaseGenerationLock(userId, lockId),
    releaseCreditLock(creditLockId, userId),
  ]);
}

/**
 * Get current guard status for a user (for UI display).
 */
export async function getGuardStatus(userId, userEmail = '') {
  if (!userId) return null;

  const isPlatformOwner = isPlatformOwnerEmail(userEmail);

  const [abuseCheck, lockCheck] = await Promise.allSettled([
    checkAbuseFlag(userId, userEmail),
    supabase?.from('generation_locks')?.select('id, job_id, created_at')?.eq('user_id', userId)?.eq('is_active', true)?.single(),
  ]);

  const entry = _getRateLimitEntry(userId);
  const now = Date.now();
  entry.timestamps = entry?.timestamps?.filter((t) => now - t < 3600_000);

  return {
    isPlatformOwner,
    isAbuseFlagged: abuseCheck?.status === 'fulfilled' ? abuseCheck?.value?.flagged : false,
    hasActiveLock: lockCheck?.status === 'fulfilled' && lockCheck?.value?.data != null,
    rateLimitCounts: {
      lastMinute: entry?.timestamps?.filter((t) => now - t < 60_000)?.length,
      last10Min: entry?.timestamps?.filter((t) => now - t < 600_000)?.length,
      lastHour: entry?.timestamps?.length,
    },
    rateLimits: RATE_LIMITS,
  };
}
